import { DestroyRef, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable, of, switchMap, take } from "rxjs";
import { environment } from "src/environments/environment";
import { SaleInArea } from "src/models/estate/IResidenceSaleInArea";
import { FilterOverview, FilterType, SearchDropDownItem } from "src/models/shared/filter.model";
import { FavoriteListStates, OverviewFavoriteListsStore } from "src/stores/overview-favorite-lists.store";
import { SelectedFavoriteListsStore } from "src/stores/selected-favorite-list.store";
import { HttpDataService } from "../httpdata.service";
import { MapMode, MapStateService } from "../map/map-state.service";
import { FavoriteListDataService } from "./favorite-list-data.service";
import { StoredFiltersDataService } from "./stored-filters-data.service";

@Injectable()
export class FavoriteListExportService {
    readonly destroyRef = inject(DestroyRef);
    private readonly mapStateService = inject(MapStateService);
    readonly dataService = inject(HttpDataService);
    readonly overviewFavoriteListStore = inject(OverviewFavoriteListsStore);
    readonly selectedFavoriteListStore = inject(SelectedFavoriteListsStore);
    readonly favoriteListDataService = inject(FavoriteListDataService);
    private readonly storedFiltersService = inject(StoredFiltersDataService);

    postSalesToExcelUrl = `${environment.apiUrl}/Report/FavoriteListExcel`;
    createFavoriteListReportUrl = `${environment.apiUrl}/Report/FavoriteListReport`;

    isGeneratingReportSignal = signal(false);
    reportProgressSignal = signal("0%");

    exportSelectionToExcel(favorite: FilterOverview): void {
        if (this.overviewFavoriteListStore.state() === FavoriteListStates.Overview || this.selectedFavoriteListStore.numberOfUnits() > 0) {
            this.getFavoriteList(favorite.id, favorite.filterType)
                .pipe(
                    take(1),
                    switchMap((list) => this.exportListToExcel(list, favorite.name)),
                )
                .subscribe({ next: this._exportToExcelSuccess, error: this._exportToReportFailure });
        }
    }

    exportLongSaleToExcel(list: SaleInArea[]): void {
        const currentMapMode = this.mapStateService.getCurrentMapMode();
        let exportList = list;

        if (currentMapMode !== MapMode.NewDwellings) {
            exportList = this.filterLongSaleList(list as SaleInArea[]);
        }

        this.exportListToExcel(exportList, null, true)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ next: this._exportToExcelSuccess, error: this._exportToReportFailure });
    }

    exportSelectionToPdf(favorite: FilterOverview): void {
        this.startReportProgressBar(favorite.numberOfUnits);
        this.getFavoriteList(favorite.id, favorite.filterType)
            .pipe(
                take(1),
                switchMap((list) => this.exportListToPdf(list, favorite.name)),
            )
            .subscribe({ next: this._exportToPdfSuccess, error: this._exportToReportFailure });
    }

    exportLongSaleToPdf(list: SaleInArea[]): void {
        const currentMapMode = this.mapStateService.getCurrentMapMode();
        this.startReportProgressBar(list.length);
        let exportList = list;
        if (currentMapMode !== MapMode.NewDwellings) {
            exportList = this.filterLongSaleList(list as SaleInArea[]);
        }

        this.exportListToPdf(exportList, null, true)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ next: this._exportToPdfSuccess, error: this._exportToReportFailure });
    }

    exportListToExcel(exportList: SaleInArea[], title: string, isLongList: boolean = false): Observable<any> {
        const currentMapMode = this.mapStateService.getCurrentMapMode();
        title = title ? title : "Ny liste";
        let sortKey: SearchDropDownItem = JSON.parse(sessionStorage.getItem("favouriteListCurrentSortItem"));

        if (isLongList) {
            const greenSortKey: SearchDropDownItem = {
                key: JSON.parse(sessionStorage.getItem("greenBeltCurrentSortItem"))
            }
            sortKey = greenSortKey;
        }

        this.mapStateService.updateIsLoading(true);

        const exp: IFavoriteListExport[] = this.createReportExportList(exportList);

        const data = {
            complexSalesMatch: exp,
            title: title,
            reportType: currentMapMode,
            sortKey: sortKey?.key != null ? sortKey?.key: "",
        };
        return this.dataService.post(this.postSalesToExcelUrl, data);
    }

    exportListToPdf(exportList: SaleInArea[], title: string, isLongList: boolean = false): Observable<any> {
        const currentMapMode = this.mapStateService.getCurrentMapMode();
        title = title ? title : "Ny liste";

        let sortKey: SearchDropDownItem = JSON.parse(sessionStorage.getItem("favouriteListCurrentSortItem"));

      if (isLongList) {
            const greenSortKey: SearchDropDownItem = {
                key: JSON.parse(sessionStorage.getItem("greenBeltCurrentSortItem"))
            }
            sortKey = greenSortKey;
        }

        this.mapStateService.updateIsLoading(true);
        const exp: IFavoriteListExport[] = this.createReportExportList(exportList);

        const data = {
            complexSalesMatch: exp,
            title: title,
            reportType: this.mapModeModeToDataType(currentMapMode),
            sortKey: sortKey?.key != null ? sortKey?.key: "",
        };
        return this.dataService.post(this.createFavoriteListReportUrl, data);
    }

    private mapModeModeToDataType(mapMode: MapMode) {
        if (mapMode == MapMode.Market || mapMode == MapMode.Market2) return 1;
        else if (mapMode == MapMode.Properties) return 2;
        else if (mapMode == MapMode.NewDwellings) return 3;
        else return 0
    }

    private filterLongSaleList(sales: SaleInArea[]): SaleInArea[] {
        // unwanted sales
        const oblList = sales.filter((item) => item.ownershipType === "Obl_leilighet");
        const coopList = sales.filter((item) => item.ownershipType === "Borettslag" && item.housingCooperativeShareId == null);

        // filter them out
        let filteredList = sales.filter((el) => !oblList.includes(el));
        filteredList = filteredList.filter((el) => !coopList.includes(el));

        return filteredList;
    }

    private createReportExportList(items: SaleInArea[]): IFavoriteListExport[] {
        const exportList: IFavoriteListExport[] = [];

        items.forEach((v: SaleInArea) => {
            const estateExport: IFavoriteListExport = {
                estateId: v.estateId,
                addressId: v.addressId,
                unitId: v.unitId,
                housingCooperativeShareId: v.housingCooperativeShareId,
                regDate: v.regDate,
            };
            exportList.push(estateExport);
        });

        return exportList;
    }

    private _exportToExcelSuccess = (res: any) => {
        const xhr = new XMLHttpRequest();

        xhr.open("GET", "reports/loading");
        xhr.setRequestHeader("Content-Type", "text/plain");

        xhr.onload = () => {
            /* Decode base64 */
            const bin = atob(res.data[0]);
            /* Convert decoded base64 string to array buffer */
            const ab = this.convertToArrayBuffer(bin);

            const blob = new Blob([ab], { type: res.data[2] });

            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = res.data[1];
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.mapStateService.updateIsLoading(false);
            this.resetReportProgressBar();
        };

        xhr.send();
    };
    private _exportToReportFailure = () => {
        this.mapStateService.updateIsLoading(false);
        this.resetReportProgressBar();
    };

    private convertToArrayBuffer(s: string): ArrayBuffer {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i !== s.length; ++i) {
            view[i] = s.charCodeAt(i) & 0xff;
        }
        return buf;
    }

    private getFavoriteList(favoriteId: number, filterType: FilterType): Observable<SaleInArea[]> {
        if (favoriteId === this.selectedFavoriteListStore.id()) return of(this.selectedFavoriteListStore.sales());

        return this.storedFiltersService.getFilter(favoriteId).pipe(
            take(1),
            switchMap((favorite) => {
                if (!favorite.unitIds || favorite.unitIds.length === 0) return of([]);

                return this.favoriteListDataService.getPropertiesForFavorite(filterType, favorite.unitIds);
            }),
        );
    }

    private _exportToPdfSuccess = (report: any) => {
        if (!report) return;
        const binaryString = window.atob(report?.data);
        const bytes = new Uint8Array(binaryString.length);
        const uInt8Array = bytes.map((_, i) => binaryString.charCodeAt(i));
        const blob = new Blob([uInt8Array], { type: "application/pdf" });
        const newWindow = window.open("", "_blank");
        let url: string = "";

        if (window.webkitURL != null) {
            url = window.webkitURL.createObjectURL(blob);
        } else {
            url = URL.createObjectURL(blob);
        }
        newWindow.location.href = url;

        this.mapStateService.updateIsLoading(false);
        this.resetReportProgressBar();
    };

    private startReportProgressBar(numSalses: number): void {
        this.isGeneratingReportSignal.set(true);
        const conversionFactor: number = 0.022;
        const updateFrequencyInMs: number = 100;
        const targetTimeInMs = numSalses * conversionFactor * 1000;

        let elapsed = 0;
        const intervalCounter = window.setInterval(() => {
            elapsed += updateFrequencyInMs;

            if (elapsed >= targetTimeInMs) {
                window.clearInterval(intervalCounter);
                return;
            }
            this.reportProgressSignal.set(((elapsed / targetTimeInMs) * 100).toFixed(0) + "%");
        }, updateFrequencyInMs);
    }

    private resetReportProgressBar(): void {
        this.isGeneratingReportSignal.set(false);
        window.setTimeout(() => {
            this.reportProgressSignal.set("0%");
        }, 200);
    }
}

export interface IFavoriteListExport {
    unitId?: number;
    estateId?: number;
    addressId?: number;
    housingCooperativeShareId?: number;
    regDate?: string;
    // ownershipId?: number;
    projectStageEstateTypeId?: number;
}
